import React from 'react';
import { useRetentionAlerts } from '../../hooks/useRetentionAlerts';
import { Clock, AlertTriangle, Shield } from 'lucide-react';

export const DataRetentionBanner: React.FC = () => {
  const { enabled, summary, encounters, loading } = useRetentionAlerts();

  if (loading || !enabled || !summary) return null;

  const urgent = summary.expiringIn3Days;
  const upcoming = summary.expiringIn7Days;
  const unsignedCount = encounters.filter(e => !e.isSigned).length;

  if (upcoming === 0) return null;

  // Determine severity
  const isCritical = summary.expiringToday > 0 || unsignedCount > 0;
  const isWarning = urgent > 0;

  const bgColor = isCritical
    ? 'bg-red-50 border-red-200'
    : isWarning
      ? 'bg-amber-50 border-amber-200'
      : 'bg-blue-50 border-blue-200';

  const textColor = isCritical
    ? 'text-red-800'
    : isWarning
      ? 'text-amber-800'
      : 'text-blue-800';

  const accentColor = isCritical
    ? 'text-red-600'
    : isWarning
      ? 'text-amber-600'
      : 'text-blue-600';

  const IconComponent = isCritical ? AlertTriangle : isWarning ? Clock : Shield;

  return (
    <div className={`${bgColor} border rounded-lg p-4 mb-4`}>
      <div className="flex items-start gap-3">
        <IconComponent className={`h-5 w-5 mt-0.5 flex-shrink-0 ${accentColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className={`text-sm font-semibold ${textColor}`}>
              Data Retention Notice
            </h4>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isCritical ? 'bg-red-100 text-red-700' : isWarning ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
              {upcoming} encounter{upcoming !== 1 ? 's' : ''} expiring
            </span>
          </div>

          <p className={`text-sm mt-1 ${textColor}`}>
            {summary.expiringToday > 0 && (
              <span className="font-semibold">{summary.expiringToday} expiring today. </span>
            )}
            {urgent > 0 && summary.expiringToday === 0 && (
              <span>{urgent} encounter{urgent !== 1 ? 's' : ''} expiring within 3 days. </span>
            )}
            {upcoming > urgent && (
              <span>{upcoming - urgent} more within 7 days. </span>
            )}
            Connected mode data is automatically removed 30 days after recording.
          </p>

          {unsignedCount > 0 && (
            <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-800 font-medium">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              {unsignedCount} encounter{unsignedCount !== 1 ? 's have' : ' has'} NOT been sent to your EHR and will be permanently lost.
            </div>
          )}

          <div className="mt-3">
            <a
              href="/encounters"
              className={`text-sm font-medium ${accentColor} hover:underline`}
            >
              Review Encounters &rarr;
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
